/**
 * Capture / replay of BCF-style viewpoints from the Fragments viewer.
 *
 * The shape mirrors what the backend's `bcf_viewpoints` row stores
 * (camera, clipping_planes, components, model_refs). PNGs are NOT
 * captured: replay uses the locally-loaded IFC to render the live
 * view, and .bcfzip export (Phase H5) renders a snapshot client-side
 * at export time. Section box state is stored under `components.section`
 * in our own shape (orientation + extents) for accurate replay; the
 * BCF-spec `clipping_planes` array is computed only at export time.
 */
import * as THREE from 'three'
import { useAppStore } from '@core/store'
import { viewerApi } from './viewerApi'
import { resolveGuidTargets } from './resolveGuidTargets'
import {
  IDENTITY_ORIENTATION,
  type SectionLocalExtents,
  type SectionOrientation,
} from '@viewer/store/sectionSlice'
import type { Annotation } from '@viewer/store/annotationsSlice'
import { measureApi } from '@viewer/measure/measureApi'
import { semCena } from './viewpointCena'
import { largarRealceDoVer, verViewpointImportado } from '@modules/bcf/lib/bcfVerImportado'

export interface VpVec3 {
  x: number
  y: number
  z: number
}

export interface VpClippingPlane {
  location: VpVec3
  direction: VpVec3
}

export interface VpCamera {
  mode: 'perspective' | 'orthographic'
  position: VpVec3
  direction: VpVec3
  up: VpVec3
  target?: VpVec3
  field_of_view?: number
  view_to_world_scale?: number
  aspect_ratio?: number
}

export interface VpSectionState {
  enabled: boolean
  orientation: SectionOrientation
  extents: SectionLocalExtents
}

export interface VpComponents {
  visible_global_ids: string[]
  hidden_global_ids: string[]
  selected_global_ids: string[]
  default_visibility: boolean
  /** VN2R-specific: full section box state for round-trip replay. */
  section?: VpSectionState
  /** BCF `<Coloring>`: elementos pintados de uma cor (RRGGBB, sem
   *  cardinal). Os conflitos exportados pintam o A a ciano e o B a
   *  magenta, as cores do realce. */
  coloring?: VpColoring[]
}

export interface VpColoring {
  /** Hexadecimal RRGGBB, sem cardinal (o que o esquema BCF pede). */
  color: string
  global_ids: string[]
}

export interface VpModelRef {
  filename: string
  sha256: string
  schema_version?: string | null
  name?: string | null
}

// VN2R-private payload tacked onto the BCF-shaped viewpoint. These
// fields don't round-trip to .bcfzip (the BCF spec has no notion of
// length/area cotas or revision clouds) but they're preserved for
// internal viewpoints stored in IndexedDB and re-applied on load.
export interface VpLengthMeasurement {
  type: 'length'
  start: VpVec3
  end: VpVec3
}

export interface VpAreaMeasurement {
  type: 'area'
  points: VpVec3[]
}

export type VpMeasurement = VpLengthMeasurement | VpAreaMeasurement

export interface VpVn2rExtras {
  measurements?: VpMeasurement[]
  annotations?: Annotation[]
}

export interface Viewpoint {
  camera: VpCamera
  clipping_planes: VpClippingPlane[]
  components: VpComponents
  model_refs: VpModelRef[]
  vn2r_extras?: VpVn2rExtras
  /** Viewpoint importado de um ficheiro BCF. A câmara e os cortes gravados
   *  são os lidos na importação e não se usam no "Ver": relê-se o
   *  `bcfv_original` com os modelos abertos agora e aplica-se a câmara do
   *  ficheiro se for plausível, senão mostram-se os elementos (ver
   *  `modules/bcf/lib/bcfVerImportado.ts`). */
  components_only?: boolean
  /** O `.bcfv` tal como veio no ficheiro importado. Enquanto o viewpoint
   *  não for recapturado, a exportação reescreve-o sem lhe tocar, para
   *  não perder câmara, cortes nem visibilidade de quem o criou. */
  bcfv_original?: { xml: string; versao: string }
  /** Anexo que é só uma imagem: não tem vista 3D. "Ver" não mexe na
   *  câmara, no corte, nas medições nem nas anotações. */
  so_imagem?: boolean
}

export { semCena }

function v3(v: THREE.Vector3): VpVec3 {
  return { x: v.x, y: v.y, z: v.z }
}

function bcfPlanesFromSection(
  orientation: SectionOrientation,
  extents: SectionLocalExtents,
): VpClippingPlane[] {
  const o = new THREE.Vector3(...orientation.origin)
  const ax = new THREE.Vector3(...orientation.axisX)
  const ay = new THREE.Vector3(...orientation.axisY)
  const az = new THREE.Vector3(...orientation.axisZ)
  const planes: VpClippingPlane[] = []
  const addPlane = (sign: 1 | -1, axis: THREE.Vector3, t: number) => {
    const point = o.clone().addScaledVector(axis, t)
    const normal = axis.clone().multiplyScalar(sign).normalize()
    planes.push({ location: v3(point), direction: v3(normal) })
  }
  addPlane(-1, ax, extents.minX)
  addPlane(1, ax, extents.maxX)
  addPlane(-1, ay, extents.minY)
  addPlane(1, ay, extents.maxY)
  addPlane(-1, az, extents.minZ)
  addPlane(1, az, extents.maxZ)
  return planes
}

async function localIdsToGlobalIds(
  modelId: string,
  localIds: number[],
): Promise<string[]> {
  if (localIds.length === 0) return []
  const model = viewerApi.getModel(modelId)
  if (!model) return []
  try {
    // Fragments exposes a direct localId → IFC GlobalId mapping via
    // getGuidsByLocalIds (returns array of guid|null in input order).
    const guids = await model.getGuidsByLocalIds(localIds)
    const out: string[] = []
    for (const g of guids) {
      if (typeof g === 'string' && g) out.push(g)
    }
    return out
  } catch (err) {
    console.warn('[viewpoint] localIds → GlobalIds failed', err)
    return []
  }
}

/**
 * Capture the current viewer state into a viewpoint blob ready to
 * post to /topics/{id}/viewpoints (or attach to a topic-create body).
 * Returns null if no model is loaded: we don't capture viewpoints
 * for empty scenes.
 */
export async function captureViewpoint(): Promise<Viewpoint | null> {
  const world = viewerApi.getWorld()
  if (!world) return null

  const cam = world.camera.three
  const ctrls = world.camera.controls
  const target = new THREE.Vector3()
  ctrls.getTarget(target)
  const position = cam.position.clone()
  const direction = target.clone().sub(position).normalize()
  const up = cam.up.clone().normalize()

  const isPersp =
    cam.type === 'PerspectiveCamera' ||
    (cam as { isPerspectiveCamera?: boolean }).isPerspectiveCamera === true

  const camera: VpCamera = {
    mode: isPersp ? 'perspective' : 'orthographic',
    position: v3(position),
    direction: v3(direction),
    up: v3(up),
    target: v3(target),
  }
  if (isPersp) {
    camera.field_of_view = (cam as THREE.PerspectiveCamera).fov
    camera.aspect_ratio = (cam as THREE.PerspectiveCamera).aspect
  }

  const store = useAppStore.getState()

  // Group selected localIds by model so we can resolve GlobalIds in
  // batch per model.
  const byModel = new Map<string, number[]>()
  for (const t of store.selectedTargets) {
    const arr = byModel.get(t.modelId) ?? []
    arr.push(t.localId)
    byModel.set(t.modelId, arr)
  }
  const selectedGlobalIds: string[] = []
  for (const [modelId, ids] of byModel) {
    const guids = await localIdsToGlobalIds(modelId, ids)
    selectedGlobalIds.push(...guids)
  }

  // Elementos escondidos, em GlobalId, para o BCF os levar como
  // excepções à visibilidade por omissão. O estado por localId continua
  // a ir à parte (state.hidden), que é o que a reposição interna usa.
  const hiddenGlobalIds: string[] = []
  try {
    const escondidos = await viewerApi.getHiddenItemsMap()
    for (const [modelId, ids] of escondidos) {
      hiddenGlobalIds.push(...(await localIdsToGlobalIds(modelId, [...ids])))
    }
  } catch (err) {
    console.warn('[viewpoint] hidden → GlobalIds failed', err)
  }

  const components: VpComponents = {
    visible_global_ids: [],
    hidden_global_ids: [...new Set(hiddenGlobalIds)],
    selected_global_ids: selectedGlobalIds,
    default_visibility: true,
  }

  let clippingPlanes: VpClippingPlane[] = []
  if (
    store.sectionEnabled &&
    store.sectionOrientation &&
    store.sectionExtents
  ) {
    components.section = {
      enabled: true,
      orientation: store.sectionOrientation,
      extents: store.sectionExtents,
    }
    clippingPlanes = bcfPlanesFromSection(
      store.sectionOrientation,
      store.sectionExtents,
    )
  }

  const modelRefs: VpModelRef[] = store.models
    .filter((m) => m.visible)
    .map((m) => ({
      filename: m.fileName,
      sha256: m.sha256,
      schema_version: m.schemaVersion ?? null,
    }))

  // VN2R-private: capture measurements (length + area) and annotations
  // so they round-trip through viewpoint save/restore. BCF doesn't
  // have a slot for these, hence the side channel.
  const measurements: VpMeasurement[] = []
  const measureTools = measureApi.getTools()
  if (measureTools.length) {
    for (const line of measureTools.length.list) {
      measurements.push({
        type: 'length',
        start: v3(line.start),
        end: v3(line.end),
      })
    }
  }
  if (measureTools.area) {
    for (const a of measureTools.area.list) {
      const pts: VpVec3[] = []
      for (const p of a.points) pts.push(v3(p))
      if (pts.length >= 3) measurements.push({ type: 'area', points: pts })
    }
  }
  const annotations = store.annotations.slice()

  const extras: VpVn2rExtras = {}
  if (measurements.length) extras.measurements = measurements
  if (annotations.length) extras.annotations = annotations

  return {
    camera,
    clipping_planes: clippingPlanes,
    components,
    model_refs: modelRefs,
    ...(measurements.length || annotations.length
      ? { vn2r_extras: extras }
      : {}),
  }
}

// Selection resolution (GlobalId → localIds, expanding non-geometric
// grouping entities to their members) lives in the shared
// resolveGuidTargets so BCF viewpoints and the FireBIM handoff resolve
// components the exact same way.

/**
 * Apply a viewpoint to the live viewer. Best-effort: missing models
 * are skipped (caller is expected to surface a "load X.ifc" hint),
 * unknown GlobalIds in the selection set are ignored.
 *
 * Um viewpoint importado de um ficheiro BCF (`components_only`) vai para
 * `verViewpointImportado`: relê o `.bcfv` original com os modelos
 * abertos e aplica a câmara e os cortes do ficheiro quando caem junto
 * dos modelos; senão mostra os elementos (selecção, cores, fantasma e
 * enquadramento). Ver `modules/bcf/lib/bcfVerImportado.ts`.
 */
export async function applyViewpoint(vp: Viewpoint): Promise<void> {
  const world = viewerApi.getWorld()
  if (!world) return

  // Uma imagem anexada não tem vista para repor. Aplicá-la como vista
  // levava a câmara para a origem e apagava anotações, medições e corte.
  if (semCena(vp)) {
    useAppStore.getState().pushToast?.(
      'Este anexo é uma imagem e não tem vista 3D associada.',
      { type: 'info' },
    )
    return
  }

  // As cores e o fantasma de um "Ver" anterior de um importado saem.
  await largarRealceDoVer()

  if (vp.components_only) {
    await verViewpointImportado(vp)
    return
  }

  // Camera first so the user sees motion start while we resolve GUIDs.
  const ctrls = world.camera.controls
  const pos = vp.camera.position
  const tgt =
    vp.camera.target ??
    {
      x: pos.x + vp.camera.direction.x * 50,
      y: pos.y + vp.camera.direction.y * 50,
      z: pos.z + vp.camera.direction.z * 50,
    }
  try {
    await ctrls.setLookAt(pos.x, pos.y, pos.z, tgt.x, tgt.y, tgt.z, true)
  } catch (err) {
    console.warn('[viewpoint] setLookAt failed', err)
  }

  // Section box: prefer our VN2R-specific section state for accurate
  // round-trip; fall back to disabling the section if absent.
  const store = useAppStore.getState()
  const section = vp.components.section
  if (section && section.enabled) {
    store.setSectionOrientation(section.orientation ?? IDENTITY_ORIENTATION)
    store.setSectionExtents(section.extents)
    store.setSectionEnabled(true)
  } else {
    store.setSectionEnabled(false)
  }

  // Selection: resolve GlobalIds → localIds per loaded model that
  // matches one of the viewpoint's model_refs by sha256.
  const refByHash = new Map(vp.model_refs.map((m) => [m.sha256, m]))
  const targets: { modelId: string; localId: number }[] = []
  const guids = vp.components.selected_global_ids
  if (guids.length > 0) {
    for (const m of store.models) {
      // model_refs e uma extensao vn2r escrita pelo NOSSO capture. Um BCF de
      // terceiros (FireBIM, outras ferramentas) nao a traz - nesse caso tenta
      // TODOS os modelos carregados em vez de nao selecionar nada (Rui, 2026-07-17).
      if (refByHash.size > 0 && !refByHash.has(m.sha256)) continue
      const localIds = await resolveGuidTargets(m.id, guids)
      for (const lid of localIds) {
        targets.push({ modelId: m.id, localId: lid })
      }
    }
  }
  // VN2R-private: restore measurements and annotations. We wipe the
  // current set first so applying a viewpoint reproduces the captured
  // state exactly (additive merging would surprise the user).
  measureApi.clearAll()
  if (vp.vn2r_extras?.measurements?.length) {
    measureApi.restoreMeasurements(vp.vn2r_extras.measurements)
  }
  store.clearAnnotations()
  if (vp.vn2r_extras?.annotations?.length) {
    for (const a of vp.vn2r_extras.annotations) {
      store.addAnnotation(a)
    }
  }

  if (targets.length > 0) {
    store.applyBulkSelection(targets, 'replace')
    // Re-paint the highlighter so the user sees the selection.
    const highlighter = viewerApi.getHighlighter()
    if (highlighter) {
      const grouped = new Map<string, Set<number>>()
      for (const t of targets) {
        const set = grouped.get(t.modelId) ?? new Set<number>()
        set.add(t.localId)
        grouped.set(t.modelId, set)
      }
      const payload: Record<string, Set<number>> = {}
      for (const [k, v] of grouped) payload[k] = v
      try {
        await highlighter.highlightByID('select', payload, true, false)
      } catch (err) {
        console.debug('[viewpoint] highlightByID failed', err)
      }
    }
  } else {
    store.clearSelection()
  }
}

/**
 * Returns true if every model_refs entry has a matching loaded model
 * (by sha256). Used by the "open viewpoint" button to warn the user
 * when the IFC isn't loaded yet.
 */
export function viewpointModelsAvailable(vp: Viewpoint): {
  ready: boolean
  missing: VpModelRef[]
} {
  const loadedHashes = new Set(useAppStore.getState().models.map((m) => m.sha256))
  const missing = vp.model_refs.filter((m) => !loadedHashes.has(m.sha256))
  return { ready: missing.length === 0, missing }
}
